import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { useLanguage } from '../../i18n/LanguageContext';
import './CardTemplates.css';
import IdCardsAdminNav from '../../components/IdCardsAdminNav/IdCardsAdminNav';

const CardTemplates = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        card_type: '',
        active_only: true
    });

    useEffect(() => {
        fetchTemplates();
    }, [filters]);

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            const response = await api.getCardTemplates(filters);
            setTemplates(response.data || []);
        } catch (err) {
            console.error('Failed to fetch templates', err);
            alert(t('Failed to load templates'));
        } finally {
            setLoading(false);
        }
    };

    const handleSetDefault = async (templateId) => {
        if (!window.confirm(t('Set this template as default?'))) return;

        try {
            await api.setDefaultCardTemplate(templateId);
            alert(t('Default template updated successfully'));
            fetchTemplates();
        } catch (err) {
            console.error('Failed to set default', err);
            alert(err.message || t('Failed to set default template'));
        }
    };

    const handleDelete = async (templateId) => {
        if (!window.confirm(t('Are you sure you want to delete this template?'))) return;

        try {
            await api.deleteCardTemplate(templateId);
            alert(t('Template deleted successfully'));
            fetchTemplates();
        } catch (err) {
            console.error('Failed to delete template', err);
            alert(err.message || t('Failed to delete template'));
        }
    };

    const getCardTypeBadge = (type) => {
        const typeMap = {
            all: { label: t('All Cards'), class: 'badge-primary' },
            standard: { label: t('Standard'), class: 'badge-info' },
            premium: { label: t('Premium'), class: 'badge-warning' }
        };
        const config = typeMap[type] || typeMap.all;
        return <span className={`badge ${config.class}`}>{config.label}</span>;
    };

    return (
        <div className="card-templates-page">
            <IdCardsAdminNav />
            <div className="page-header">
                <div>
                    <h2>{t('Card Templates')}</h2>
                    <p className="text-muted">{t('Manage ID card design templates')}</p>
                </div>
                <button
                    className="btn btn-primary"
                    onClick={() => navigate('/card-templates/create')}
                >
                    + {t('Create New Template')}
                </button>
            </div>

            <div className="filters-container">
                <div className="row">
                    <div className="col-md-3">
                        <label>{t('Card Type')}</label>
                        <select
                            className="form-control"
                            value={filters.card_type}
                            onChange={(e) => setFilters({ ...filters, card_type: e.target.value })}
                        >
                            <option value="">{t('All Types')}</option>
                            <option value="standard">{t('Standard')}</option>
                            <option value="premium">{t('Premium')}</option>
                        </select>
                    </div>
                    <div className="col-md-3">
                        <label>{t('Status')}</label>
                        <select
                            className="form-control"
                            value={filters.active_only ? 'active' : 'all'}
                            onChange={(e) => setFilters({ ...filters, active_only: e.target.value === 'active' })}
                        >
                            <option value="active">{t('Active Only')}</option>
                            <option value="all">{t('All')}</option>
                        </select>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">{t('Loading...')}</span>
                    </div>
                </div>
            ) : templates.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">📄</div>
                    <h3>{t('No Templates Found')}</h3>
                    <p>{t('Create your first card template to get started')}</p>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate('/card-templates/create')}
                    >
                        {t('Create Template')}
                    </button>
                </div>
            ) : (
                <div className="templates-grid">
                    {templates.map(template => (
                        <div key={template.id} className="template-card">
                            <div className="template-card-header">
                                <h3>{template.name}</h3>
                                {template.is_default && (
                                    <span className="badge badge-success">{t('Default')}</span>
                                )}
                            </div>

                            <p className="template-description">
                                {template.description || t('No description')}
                            </p>

                            <div className="template-meta">
                                <div className="meta-item">
                                    <span className="meta-label">{t('Type:')}</span>
                                    {getCardTypeBadge(template.card_type)}
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">{t('Status:')}</span>
                                    <span className={`badge ${template.is_active ? 'badge-success' : 'badge-secondary'}`}>
                                        {template.is_active ? t('Active') : t('Inactive')}
                                    </span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">{t('Side:')}</span>
                                    <span className="badge badge-info">{template.template_type}</span>
                                </div>
                            </div>

                            <div className="template-actions">
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => navigate(`/card-templates/edit/${template.id}`)}
                                >
                                    ✏️ {t('Edit')}
                                </button>
                                {!template.is_default && (
                                    <button
                                        className="btn btn-sm btn-outline-success"
                                        onClick={() => handleSetDefault(template.id)}
                                    >
                                        ⭐ {t('Set Default')}
                                    </button>
                                )}
                                {!template.is_default && (
                                    <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDelete(template.id)}
                                    >
                                        🗑️ {t('Delete')}
                                    </button>
                                )}
                            </div>

                            <div className="template-timestamp">
                                {t('Created:')} {new Date(template.created_at).toLocaleDateString()}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CardTemplates;
